import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { trackEvent, getLastVisitDays, updateLastVisit, getReferrerSource, getAcquisitionSource } from './utils/analytics';
import { getGradeNumber } from './utils/gradeUtils';
import { supabase } from './services/supabaseClient';
import {
  fetchChildren, createChild, updateChild, deleteChild,
  saveCurrentChildId, loadCurrentChildId,
  loadGuestProfiles, saveGuestProfile, updateGuestProfile, deleteGuestProfile,
  getGuestCurrentId, setGuestCurrentId,
} from './services/childrenApi';
import AuthScreen           from './components/AuthScreen';
import ResetPasswordScreen  from './components/ResetPasswordScreen';
import ProfileLogin    from './components/ProfileLogin';
import ProfileEdit     from './components/ProfileEdit';
import ProfileSelect   from './components/ProfileSelect';
import SiblingManage   from './components/SiblingManage';
import HomeScreen      from './components/HomeScreen';
import TextSelect      from './components/TextSelect';
import ModeSelect      from './components/ModeSelect';
import RenshyuMode     from './components/RenshyuMode';
import HonbanMode      from './components/HonbanMode';
import ScoreScreen     from './components/ScoreScreen';
import ShootingGame    from './components/ShootingGame';
import RecordScreen    from './components/RecordScreen';
import RankingScreen   from './components/RankingScreen';
import InfoScreen      from './components/InfoScreen';
import FeedbackForm    from './components/FeedbackForm';
import NoticeBoard, { hasImportantUnread } from './components/NoticeBoard';
import AssignmentView  from './components/AssignmentView';
import TeacherScreen   from './components/TeacherScreen';
import StudentScreen   from './components/StudentScreen';
import MonthlyRecap, { calcMonthlyStats } from './components/MonthlyRecap';
import { loadStats, getRecords } from './utils/storage';
import { migrateLogs, getCloudStats } from './services/readingLogsApi';
import { syncCustomTextsOnLogin } from './services/customTextsApi';
import { createCheckoutSession, createCreditCheckoutSession } from './services/stripeApi';
import { fetchAICredits, consumeAICredit } from './services/creditApi';

// 画面遷移
// auth → profileSetup/profileSelect → home → textSelect → renshyu/honban → score → shooting → home
//                                       ↕ record ↕ ranking ↕ info ↕ siblingManage (BottomNav)

export default function App() {
  const [screen, setScreen]             = useState('loading');
  const [stats, setStats]               = useState(loadStats());
  const [stampMap, setStampMap]         = useState(null); // クラウド用カレンダーデータ
  const [scoreData, setScoreData]       = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [showNotice, setShowNotice]       = useState(false);
  const [showNoticeAll, setShowNoticeAll] = useState(false);
  const [monthlyRecap, setMonthlyRecap] = useState(null); // { year, month, totalDays, maxStreak }

  // プロフィール関連
  const [familyId, setFamilyId]         = useState(null); // Supabase auth user id
  const [isGuest, setIsGuest]           = useState(false);
  const [isPremium, setIsPremium]       = useState(false);
  const [isAdmin, setIsAdmin]           = useState(false); // クーポン発行フォームの表示可否
  const [children, setChildren]         = useState([]);
  const [currentChild, setCurrentChild] = useState(null);
  const [showProfileSelect, setShowProfileSelect] = useState(false);

  const [aiCredits, setAiCredits]           = useState(0);    // クレジット残数
  const [paymentMessage, setPaymentMessage] = useState(null); // 'success' | 'cancel' | 'credits_success' | null
  const [streakCelebration, setStreakCelebration] = useState(null); // { childName, avatar, streak }
  const [authInitialMode, setAuthInitialMode] = useState('top'); // auth画面の初期モード
  const [creditLoginPrompt, setCreditLoginPrompt] = useState(null); // null | '5' | '10'
  const [assignmentCode, setAssignmentCode] = useState(null); // /t/:code 経由の配布課題コード
  // 先生ページへの入り方。'inapp' = 設定から遷移（戻るときは画面遷移だけで済む）
  // 'direct' = URL直打ちでいきなり /teacher（この場合セッション・プロフィール未ロード）
  const [teacherEntry, setTeacherEntry] = useState('direct');
  // 生徒ページも同様。設定から来たときだけ画面遷移で戻れる
  const [studentEntry, setStudentEntry] = useState('direct');
  // 配布課題を開いたときの戻り先。URL直打ちの場合は null（アプリの画面が未ロードのため）
  const [assignmentReturn, setAssignmentReturn] = useState(null);
  // pendingUpgrade は localStorage で管理（Reactクロージャ問題を回避）
  // localStorage.getItem('pendingUpgrade') === '1' のとき有効

  const refreshStats = () => {
    const s = loadStats();
    setStats(s);
    trackEvent('ondoku_streak_updated', { streak: s.streak, grade: getGradeNumber(currentChild?.birth_year, currentChild?.birth_month) ?? 'unknown' });
  };
  const goTo = (s) => {
    setScreen(s);
    // auth以外の画面に遷移したらモードをリセット
    if (s !== 'auth') setAuthInitialMode('top');
  };

  // クラウド統計を更新（ログイン済みのchildIdが確定してから呼ぶ）
  const refreshCloudStats = useCallback(async (childId) => {
    if (!childId) return null;
    const cloudStats = await getCloudStats(childId);
    if (cloudStats) {
      setStats({ streak: cloudStats.streak, totalStars: cloudStats.totalStars, monthDays: cloudStats.monthDays });
      setStampMap(cloudStats.stampMap);
      trackEvent('ondoku_streak_updated', { streak: cloudStats.streak, grade: getGradeNumber(currentChild?.birth_year, currentChild?.birth_month) ?? 'unknown' });
    }
    return cloudStats;
  }, [currentChild]); // eslint-disable-line react-hooks/exhaustive-deps

  // プロフィール選択画面をスキップできるか（1人＆PINなし）
  const canSkipProfileLogin = (profiles) =>
    profiles.length === 1 && !profiles[0].pin;

  // ── プロフィール読み込み ─────────────────────────
  const loadProfiles = useCallback(async (guest) => {
    if (guest) {
      const profiles = loadGuestProfiles();
      setChildren(profiles);
      const currentId = getGuestCurrentId();
      const current = profiles.find(p => p.id === currentId) ?? profiles[0] ?? null;
      setCurrentChild(current);
      return profiles;
    } else {
      const profiles = await fetchChildren();
      setChildren(profiles);
      const currentId = loadCurrentChildId();
      const current = profiles.find(p => p.id === currentId) ?? profiles[0] ?? null;
      setCurrentChild(current);
      // ログイン済みはクラウドから統計を取得
      if (current) await refreshCloudStats(current.id);
      return profiles;
    }
  }, [refreshCloudStats]);

  // ── セッション確認（起動時）──────────────────────
  useEffect(() => {
    // 配布リンク（/t/:code）は認証フローを完全にスキップする
    const assignmentMatch = window.location.pathname.match(/^\/t\/([a-zA-Z0-9]+)/);
    if (assignmentMatch) {
      setAssignmentCode(assignmentMatch[1]);
      setAssignmentReturn(null); // アプリの画面が未ロードなので戻り先がない
      goTo('assignment');
      return;
    }

    // 先生ページ（/teacher）は通常のプロフィール選択フローに乗せない
    if (window.location.pathname.startsWith('/teacher')) {
      setTeacherEntry('direct');
      goTo('teacher');
      return;
    }

    // 生徒ページ（/s）もログイン不要。認証フローをスキップする
    // （startsWith('/s') だと将来 /settings などと衝突するので厳密に判定）
    if (/^\/s(\/|$)/.test(window.location.pathname)) {
      setStudentEntry('direct');
      goTo('student');
      return;
    }

    // URLを同期的に先読み（非同期コールバック内では別のuseEffectがreplaceStateで消している）
    const isPaymentSuccess = new URLSearchParams(window.location.search).get('payment') === 'success';

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // ログイン済み: プレミアム確認 → プロフィール読み込み
        const { data } = await supabase
          .from('families')
          .select('is_premium, is_admin')
          .eq('id', session.user.id)
          .single();
        const premiumStatus = data?.is_premium || data?.is_admin || false;
        setIsPremium(premiumStatus);
        setIsAdmin(data?.is_admin || false);
        setFamilyId(session.user.id);
        setIsGuest(false);

        // カスタムテキストをクラウドと同期
        syncCustomTextsOnLogin(session.user.id);

        // AI採点クレジット残数を取得
        fetchAICredits(session.user.id).then(setAiCredits);

        // セッション開始イベント（is_premiumが確定してから送る）
        const days = getLastVisitDays();
        trackEvent('ondoku_session_start', {
          is_returning:      days >= 0,
          days_since_last:   days,
          is_premium:        premiumStatus,
          has_name:          'yes', // ログイン済みは必ずプロフィール設定済み
          auth_state:        'logged_in',
          referrer_source:   getReferrerSource(),    // 今回の流入元
          acquisition_source: getAcquisitionSource(), // 初回流入元（継続追跡用）
        });
        updateLastVisit();

        // Googleログインから戻ってきた場合はログイン/登録イベントを送る
        // （メール認証は AuthScreen 側で送信済み。ここはOAuthの復帰時のみ）
        if (localStorage.getItem('ondoku_pending_oauth') === '1') {
          localStorage.removeItem('ondoku_pending_oauth');
          if (data) trackEvent('ondoku_login', { method: 'google' });
          else      trackEvent('ondoku_signup', { method: 'google', plan: 'free' });
        }

        // 決済完了後: ゲストプロフィール・きろくを自動移行（プロフィール未作成のとき）
        if (isPaymentSuccess) {
          const existingProfiles = await fetchChildren();
          if (existingProfiles.length === 0) {
            // families レコードが未作成の場合は作成
            if (!data) {
              await supabase.from('families').insert({ id: session.user.id, pin: '', is_premium: false });
            }
            // ゲストのプロフィールをそのままクラウドに作成
            const guestProfiles = loadGuestProfiles();
            for (const gp of guestProfiles) {
              const saved = await createChild({
                name: gp.name,
                avatar: gp.avatar,
                birth_year: gp.birth_year,
                birth_month: gp.birth_month,
              });
              if (saved) {
                const localLogs = getRecords();
                if (localLogs.length > 0) await migrateLogs(localLogs, saved.id);
              }
            }
          }
        }

        const profiles = await loadProfiles(false);
        if (profiles.length === 0) goTo('profileSetup');
        else if (canSkipProfileLogin(profiles)) goTo('home');
        else goTo('profileLogin');
      } else {
        // 未ログイン（ゲスト）: is_premium=false で送る
        const days = getLastVisitDays();
        // ゲストのプロフィール名を同期的に確認（「ゲスト」のままか名前を設定しているか）
        const guestProfiles = loadGuestProfiles();
        const guestCurrentId = getGuestCurrentId();
        const guestChild = guestProfiles.find(p => p.id === guestCurrentId) ?? guestProfiles[0] ?? null;
        const guestHasName = !!(guestChild?.name && guestChild.name !== 'ゲスト');
        trackEvent('ondoku_session_start', {
          is_returning:       days >= 0,
          days_since_last:    days,
          is_premium:         false,
          has_name:           guestHasName ? 'yes' : 'no',
          auth_state:         'guest',
          referrer_source:    getReferrerSource(),
          acquisition_source: getAcquisitionSource(),
        });
        updateLastVisit();
        // ?auth=register で来たらトップの3択を飛ばして登録フォームへ
        // （先生ページなど、外から「アカウントを作る」導線で来る場合に使う）
        if (new URLSearchParams(window.location.search).get('auth') === 'register') {
          setAuthInitialMode('free-register');
        }
        goTo('auth');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // パスワードリセットメールのリンクをクリックして戻ってきた
        goTo('resetPassword');
        return;
      }
      if (!session) {
        setIsGuest(false);
        setCurrentChild(null);
        setChildren([]);
        goTo('auth');
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Stripe決済後のリダイレクト検知
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      setPaymentMessage('success');
      trackEvent('ondoku_premium_purchased');
      // 5秒後にトーストを自動消去
      setTimeout(() => setPaymentMessage(null), 5000);
      // URLからパラメータを消す
      window.history.replaceState({}, '', window.location.pathname);
      // Webhookの処理を待って（3秒後）プレミアム状態を再取得、さらに8秒後に再試行
      const fetchPremium = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data } = await supabase
            .from('families')
            .select('is_premium, is_admin')
            .eq('id', session.user.id)
            .single();
          if (data?.is_premium || data?.is_admin) {
            setIsPremium(true);
            return true;
          }
        }
        return false;
      };
      // 3秒後に1回目
      setTimeout(async () => {
        const done = await fetchPremium();
        // まだ反映されてなければ8秒後に再試行
        if (!done) setTimeout(fetchPremium, 5000);
      }, 3000);
    } else if (payment === 'credits_success') {
      setPaymentMessage('credits_success');
      trackEvent('ondoku_credit_purchased');
      setTimeout(() => setPaymentMessage(null), 5000);
      window.history.replaceState({}, '', window.location.pathname);
      // Webhook処理を待ってクレジットを再取得
      const refetchCredits = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const credits = await fetchAICredits(session.user.id);
          setAiCredits(credits);
        }
      };
      setTimeout(refetchCredits, 3000);
      setTimeout(refetchCredits, 8000);
    } else if (payment === 'cancel') {
      setPaymentMessage('cancel');
      trackEvent('ondoku_checkout_abandoned');
      setTimeout(() => setPaymentMessage(null), 4000);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ホーム画面表示時にお知らせポップアップ
  useEffect(() => {
    if (screen === 'home' && hasImportantUnread()) setShowNotice(true);
  }, [screen]);

  // 月が変わったら前月サマリーを表示（1回のみ）
  useEffect(() => {
    if (screen !== 'home') return;
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const prevYear  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const recapKey  = `ondoku_recap_shown_${prevYear}_${prevMonth + 1}`;
    if (localStorage.getItem(recapKey)) return; // 既に表示済み

    // stampMap or localStorageから前月データを取得
    const map = stampMap ?? (() => {
      const records = getRecords();
      const m = {};
      records.forEach(r => {
        const d = r.date ? r.date.slice(0, 10) : null;
        if (d) m[d] = Math.max(m[d] ?? 0, r.totalStars ?? 0);
      });
      return m;
    })();

    const result = calcMonthlyStats(map, prevYear, prevMonth);
    if (result && result.totalDays > 0) {
      setMonthlyRecap({ year: prevYear, month: prevMonth, ...result });
    }
    localStorage.setItem(recapKey, '1');
  }, [screen, stampMap]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── プロフィールログイン選択 ─────────────────────
  const handleProfileLogin = async (child) => {
    setCurrentChild(child);
    if (isGuest) {
      setGuestCurrentId(child.id);
    } else {
      saveCurrentChildId(child.id);
      await refreshCloudStats(child.id);
    }
    goTo('home');
  };

  // ── ゲスト開始 ───────────────────────────────────
  const handleGuest = async () => {
    setIsGuest(true);
    setIsPremium(false);
    const profiles = loadGuestProfiles();
    setChildren(profiles);
    if (profiles.length === 0) {
      goTo('profileSetup');
    } else if (canSkipProfileLogin(profiles)) {
      // 1人＆PINなし → 直接ホーム
      const currentId = getGuestCurrentId();
      const current = profiles.find(p => p.id === currentId) ?? profiles[0];
      setCurrentChild(current);
      goTo('home');
    } else {
      // PINあり or 複数プロフィール → 選択画面
      goTo('profileLogin');
    }
  };

  // ── ログイン後 ───────────────────────────────────
  const handleLoggedIn = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase
      .from('families')
      .select('is_premium, is_admin')
      .eq('id', session.user.id)
      .single();
    setIsPremium(data?.is_premium || data?.is_admin || false);
    setIsAdmin(data?.is_admin || false);
    setIsGuest(false);

    const profiles = await loadProfiles(false);

    // 新規登録 or プレミアム登録フロー → 即Stripe決済へ
    // （localStorage フラグ OR プロフィール未作成 ＝ 新規アカウント）
    // ただし freeRegister フラグが立っている場合は無料登録なのでSkip
    const isPending = localStorage.getItem('pendingUpgrade') === '1';
    const pendingSource = localStorage.getItem('pendingUpgradeSource') || 'new_account_default';
    const isFreeRegister = localStorage.getItem('freeRegister') === '1';
    localStorage.removeItem('freeRegister');
    if (!isFreeRegister && (isPending || profiles.length === 0) && !data?.is_premium && !data?.is_admin) {
      localStorage.removeItem('pendingUpgrade');
      localStorage.removeItem('pendingUpgradeSource');
      try {
        const url = await createCheckoutSession(session.user.id, session.user.email);
        trackEvent('ondoku_checkout_started', { source: pendingSource, via: 'post_register' });
        window.location.href = url;
      } catch (err) {
        alert('決済画面を開けませんでした。もう一度試してください。');
        goTo('profileSetup');
      }
      return;
    }
    localStorage.removeItem('pendingUpgrade');
    localStorage.removeItem('pendingUpgradeSource');

    // ゲスト時のきろくをクラウドに移行（ログイン直後1回だけ）
    const localLogs = getRecords();
    if (localLogs.length > 0 && profiles.length > 0) {
      await migrateLogs(localLogs, profiles[0].id);
    }

    if (profiles.length === 0) goTo('profileSetup');
    else if (canSkipProfileLogin(profiles)) goTo('home');
    else goTo('profileLogin');
  };

  // ── プロフィール保存 ─────────────────────────────
  const handleProfileSave = async (profile) => {
    let saved;
    if (isGuest) {
      saved = saveGuestProfile(profile);
      setGuestCurrentId(saved.id);
      setCurrentChild(saved);
      setChildren(prev => [...prev.filter(p => p.id !== saved.id), saved]);
    } else {
      // families レコードがなければ先に作成
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: fam } = await supabase
          .from('families').select('id').eq('id', user.id).single();
        if (!fam) {
          await supabase.from('families').insert({ id: user.id, pin: '', is_premium: false });
        }
      }
      saved = await createChild(profile);
      if (saved) {
        saveCurrentChildId(saved.id);
        // 新規登録直後のプロフィール作成時：ゲスト時のlocalStorageきろくを移行
        const localLogs = getRecords();
        if (localLogs.length > 0) {
          await migrateLogs(localLogs, saved.id);
        }
      } else {
        console.error('createChild failed');
        alert('プロフィールの保存に失敗しました。もう一度試してください。');
        return;
      }
    }
    await loadProfiles(isGuest);
    goTo('home');
  };

  // ── プロフィールスキップ（ゲストのみ） ──────────
  const handleProfileSkip = () => {
    const defaultProfile = { id: `guest_${Date.now()}`, name: 'ゲスト', avatar: '🐣', birth_year: null, birth_month: null };
    if (isGuest) {
      const saved = saveGuestProfile(defaultProfile);
      setGuestCurrentId(saved.id);
      setCurrentChild(saved);
      setChildren([saved]);
    }
    goTo('home');
  };

  // ── 連続ポップアップの表示済み管理 ──────────────
  const STREAK_POPUP_KEY = (childId) => `streakPopupDate_${childId}`;
  const todayStr = () => new Date().toLocaleDateString('sv-SE'); // 'YYYY-MM-DD'
  const markStreakPopupShown = (childId) =>
    localStorage.setItem(STREAK_POPUP_KEY(childId), todayStr());
  const hasStreakPopupShownToday = (childId) =>
    localStorage.getItem(STREAK_POPUP_KEY(childId)) === todayStr();

  // ── プロフィール切り替え ─────────────────────────
  const handleSelectChild = async (child) => {
    setCurrentChild(child);
    if (isGuest) setGuestCurrentId(child.id);
    else {
      saveCurrentChildId(child.id);
      const cloudStats = await refreshCloudStats(child.id); // 切り替え時に統計も更新
      if (cloudStats && !hasStreakPopupShownToday(child.id)) {
        setStreakCelebration({ childName: child.name, avatar: child.avatar ?? '🐣', streak: cloudStats.streak });
        setTimeout(() => setStreakCelebration(null), 3000);
      }
    }
    setShowProfileSelect(false);
  };

  // ── きょうだい管理 ───────────────────────────────
  const handleSiblingAdd = async (profile) => {
    if (isGuest) {
      const saved = saveGuestProfile(profile);
      await loadProfiles(true);
      if (!currentChild) setCurrentChild(saved);
    } else {
      // families レコードがなければ先に作成
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: fam } = await supabase
          .from('families').select('id').eq('id', user.id).single();
        if (!fam) {
          await supabase.from('families').insert({ id: user.id, pin: '', is_premium: false });
        }
      }
      const saved = await createChild(profile);
      if (!saved) {
        alert('プロフィールの保存に失敗しました。');
        return;
      }
      await loadProfiles(false);
      if (!currentChild) {
        setCurrentChild(saved);
        saveCurrentChildId(saved.id);
      }
    }
  };

  const handleSiblingUpdate = async (id, updates) => {
    if (isGuest) {
      updateGuestProfile(id, updates);
    } else {
      await updateChild(id, updates);
    }
    await loadProfiles(isGuest);
  };

  const handleSiblingDelete = async (id) => {
    if (isGuest) {
      deleteGuestProfile(id);
    } else {
      await deleteChild(id);
    }
    if (currentChild?.id === id) setCurrentChild(null);
    await loadProfiles(isGuest);
  };

  // ── クレジットパック購入 ──────────────────────────
  const handleBuyCredits = async (pack) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCreditLoginPrompt(pack); // ポップアップ表示
        return;
      }
      const url = await createCreditCheckoutSession(session.user.id, session.user.email, pack);
      trackEvent('ondoku_credit_checkout_started', { pack });
      window.location.href = url;
    } catch (err) {
      console.error('クレジット購入エラー:', err);
      alert('購入画面を開けませんでした。もう一度試してください。');
    }
  };

  // ── クレジット消費（HonbanMode から呼ばれる） ────
  const handleUseCredit = async () => {
    const newCount = await consumeAICredit(familyId, aiCredits);
    setAiCredits(newCount);
    return newCount;
  };

  // ── 無料登録へ誘導（ゲストのみ） ────────────────
  const handleFreeSignup = () => {
    setAuthInitialMode('free-register');
    goTo('auth');
  };

  // ── プレミアムアップグレード ─────────────────────
  const handleUpgrade = async (source = 'unknown') => {
    trackEvent('ondoku_premium_info_clicked', { source });
    if (isGuest) {
      localStorage.setItem('pendingUpgrade', '1');
      localStorage.setItem('pendingUpgradeSource', source);
      setAuthInitialMode('register');
      goTo('auth');
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const url = await createCheckoutSession(session.user.id, session.user.email);
      trackEvent('ondoku_checkout_started', { source, via: 'direct' });
      window.location.href = url;
    } catch (err) {
      console.error('Checkout エラー:', err);
      alert('決済画面を開けませんでした。もう一度試してください。');
    }
  };

  // ── ログアウト ───────────────────────────────────
  const handleLogout = async () => {
    await supabase.auth.signOut();
    // ログアウト時にカスタムテキストをlocalStorageから削除
    // （ゲストモードで他のユーザーのテキストが見えないようにするため）
    // ログイン時に syncCustomTextsOnLogin でSupabaseから復元される
    localStorage.removeItem('ondoku_custom_texts');
    setCurrentChild(null);
    setChildren([]);
    setIsGuest(false);
    goTo('auth');
  };

  // ── BottomNav ────────────────────────────────────
  const handleNav = (target) => {
    if (target === 'home')           { refreshStats(); if (!isGuest && currentChild?.id) refreshCloudStats(currentChild.id); goTo('home'); }
    else if (target === 'textSelect')  goTo('textSelect');
    else if (target === 'record')      goTo('record');
    else if (target === 'ranking')     goTo('ranking');
    else if (target === 'info')        goTo('info');
    else if (target === 'auth')        goTo('auth');
    else if (target === 'feedback')    goTo('feedback');
    else if (target === 'siblings')    goTo('siblingManage');
  };

  const handleScoreComplete = (data) => {
    setScoreData(data);
    goTo('score');
    // ログイン済みならクラウド統計も即更新
    if (!isGuest && currentChild?.id) {
      refreshCloudStats(currentChild.id);
      // 本番完了 → 今日のポップアップは不要（達成済み）
      if (data?.mode === 'honban') markStreakPopupShown(currentChild.id);
    }
  };
  const handleGameDone = () => { refreshStats(); if (!isGuest && currentChild?.id) refreshCloudStats(currentChild.id); goTo('home'); };
  // localStorageからSupabaseへの自動移行完了時にカレンダー統計を更新
  const handleRecordMigrateComplete = useCallback(() => {
    refreshStats();
    if (!isGuest && currentChild?.id) refreshCloudStats(currentChild.id);
  }, [isGuest, currentChild?.id, refreshCloudStats]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="app-root">

      {/* 配布課題。/t/:code から直接来る場合と、アプリ内の「せんせいからのかだい」から来る場合がある */}
      {screen === 'assignment' && (
        <AssignmentView
          code={assignmentCode}
          // アプリ内から開いたときだけ子の情報を渡す（名前入力を飛ばし、きろくにも残す）
          childId={currentChild?.id ?? null}
          childName={!isGuest ? (currentChild?.name ?? null) : null}
          // 「生徒ページ」ボタンの遷移先。課題一覧と自分のきろくが揃っている
          onDone={() => {
            if (currentChild) {
              refreshStats();
              if (!isGuest && currentChild.id) refreshCloudStats(currentChild.id);
            }
            window.history.pushState({}, '', '/s');
            goTo('student');
          }}
          // 「もどる」の行き先。開いた画面へ帰す（URL直打ちの場合は null）
          onBack={
            assignmentReturn
              ? () => {
                  window.history.pushState({}, '', assignmentReturn === 'student' ? '/s' : '/app');
                  goTo(assignmentReturn);
                }
              : null
          }
        />
      )}

      {/* 生徒ページ（/s）：ログイン不要 */}
      {screen === 'student' && (
        <StudentScreen
          childId={currentChild?.id ?? null}
          onOpenAssignment={(code) => {
            window.history.pushState({}, '', `/t/${code}`);
            setAssignmentCode(code);
            setAssignmentReturn('student');
            goTo('assignment');
          }}
          onBack={
            // 設定から来た場合のみ画面遷移で戻る（URL直打ちはプロフィール未ロード）
            studentEntry === 'inapp'
              ? () => { window.history.pushState({}, '', '/app'); goTo('info'); }
              : null
          }
          onHome={() => {
            // プロフィールが読み込まれていれば画面遷移だけ。
            // URL直打ちで来た場合はアプリを起動し直す必要がある
            if (currentChild) {
              window.history.pushState({}, '', '/app');
              goTo('home');
            } else {
              window.location.href = '/app';
            }
          }}
        />
      )}

      {/* 先生ページ（/teacher）：ログイン必須 */}
      {screen === 'teacher' && (
        <TeacherScreen
          onBack={
            // 設定から来た場合は画面遷移だけで戻る（リロードするとプロフィール選択からやり直しになる）
            teacherEntry === 'inapp'
              ? () => { window.history.pushState({}, '', '/app'); goTo('info'); }
              : null   // URL直打ちで来た場合はプロフィール未ロードなので通常の遷移が必要
          }
          onContact={
            // フィードバックフォームはプロフィール情報を使うので、in-app のときだけ渡す
            teacherEntry === 'inapp'
              ? () => { window.history.pushState({}, '', '/app'); goTo('feedback'); }
              : null
          }
          onLogin={
            // トップの3択を経由せず、無料登録フォームへ直接進ませる
            teacherEntry === 'inapp'
              ? () => {
                  window.history.pushState({}, '', '/app');
                  setAuthInitialMode('free-register');
                  goTo('auth');
                }
              : null
          }
        />
      )}

      {/* ローディング */}
      {screen === 'loading' && (
        <div className="auth-loading">
          <div className="auth-loading-logo">Ondoku Quest</div>
          <div className="auth-loading-dot" />
        </div>
      )}

      {/* 認証 */}
      {screen === 'auth' && (
        <AuthScreen
          onGuest={handleGuest}
          onLoggedIn={handleLoggedIn}
          initialMode={authInitialMode}
        />
      )}

      {/* パスワード再設定画面（リセットメールのリンクから戻ってきた時） */}
      {screen === 'resetPassword' && (
        <ResetPasswordScreen
          onDone={() => goTo('auth')}
        />
      )}

      {/* ログイン後プロフィール選択 */}
      {screen === 'profileLogin' && (
        <ProfileLogin
          children={children}
          onSelect={handleProfileLogin}
          onManage={() => goTo('siblingManage')}
          onBack={() => goTo('auth')}
        />
      )}

      {/* プロフィール初回作成 */}
      {screen === 'profileSetup' && (
        <ProfileEdit
          isGuest={isGuest}
          isFirst={true}
          onSave={handleProfileSave}
          onSkip={isGuest ? handleProfileSkip : null}
        />
      )}

      {/* ホーム */}
      {screen === 'home' && (
        <HomeScreen
          stats={stats}
          stampMap={stampMap}
          currentChild={currentChild}
          onStart={() => goTo('textSelect')}
          onNav={handleNav}
          onInfo={() => goTo('info')}
          onLogout={handleLogout}
          onProfileTap={() => setShowProfileSelect(true)}
        />
      )}

      {/* テキスト選択 */}
      {screen === 'textSelect' && (
        <TextSelect
          initialSelected={selectedText}
          isPremium={isPremium}
          aiCredits={aiCredits}
          currentChild={currentChild}
          familyId={familyId}
          isGuest={isGuest}
          onSelect={(mode, text) => { setSelectedText(text); goTo(mode); }}
          onBack={() => goTo('home')}
          onNav={handleNav}
          onUpgrade={() => handleUpgrade('limit_reached')}
          onBuyCredits={handleBuyCredits}
          onAssignment={(code) => {
            setAssignmentCode(code);
            setAssignmentReturn('textSelect');
            goTo('assignment');
          }}
        />
      )}

      {screen === 'modeSelect' && (
        <ModeSelect
          text={selectedText}
          onSelect={(mode, text) => { setSelectedText(text); goTo(mode); }}
          onBack={() => goTo('textSelect')}
        />
      )}

      {screen === 'renshyu' && (
        <RenshyuMode
          text={selectedText}
          onComplete={handleScoreComplete}
          onBack={() => goTo('textSelect')}
          childId={currentChild?.id}
          grade={getGradeNumber(currentChild?.birth_year, currentChild?.birth_month)}
        />
      )}

      {screen === 'honban' && (
        <HonbanMode
          text={selectedText}
          isPremium={isPremium}
          isGuest={isGuest}
          aiCredits={aiCredits}
          onUseCredit={handleUseCredit}
          onComplete={handleScoreComplete}
          onBack={() => goTo('textSelect')}
          childId={currentChild?.id}
          grade={getGradeNumber(currentChild?.birth_year, currentChild?.birth_month)}
        />
      )}

      {screen === 'score' && (
        <ScoreScreen
          data={scoreData}
          isPremium={isPremium}
          isGuest={isGuest}
          childId={currentChild?.id}
          onPlayGame={() => goTo('shooting')}
          onHome={() => { refreshStats(); if (!isGuest && currentChild?.id) refreshCloudStats(currentChild.id); goTo('home'); }}
          onBack={() => goTo('textSelect')}
          onRetry={scoreData?.mode === 'honban' ? () => goTo('honban') : null}
          onNav={handleNav}
          onUpgrade={() => handleUpgrade('limit_reached')}
        />
      )}

      {screen === 'shooting' && <ShootingGame onDone={handleGameDone} childId={currentChild?.id} />}
      {screen === 'record'   && <RecordScreen  onNav={handleNav} isPremium={isPremium} childId={currentChild?.id} isGuest={isGuest} currentChild={currentChild} onMigrateComplete={handleRecordMigrateComplete} />}
      {screen === 'ranking'  && <RankingScreen onNav={handleNav} isPremium={isPremium} childId={currentChild?.id} isGuest={isGuest} currentChild={currentChild} children={children} />}

      {screen === 'info' && (
        <InfoScreen
          onBack={() => goTo('home')}
          onNav={handleNav}
          onLogout={handleLogout}
          onSiblings={() => goTo('siblingManage')}
          isGuest={isGuest}
          isAdmin={isAdmin}
          onTeacher={() => {
            // リロードせず画面遷移だけ。URLは合わせておく（共有・リロード時に効く）
            window.history.pushState({}, '', '/teacher');
            setTeacherEntry('inapp');
            goTo('teacher');
          }}
          onStudent={() => {
            window.history.pushState({}, '', '/s');
            setStudentEntry('inapp');
            goTo('student');
          }}
        />
      )}

      {screen === 'feedback' && (
        <FeedbackForm onBack={() => goTo('info')} onNav={handleNav} currentChild={currentChild} isGuest={isGuest} />
      )}

      {/* きょうだい管理 */}
      {screen === 'siblingManage' && (
        <SiblingManage
          children={children}
          isGuest={isGuest}
          isPremium={isPremium}
          onAdd={handleSiblingAdd}
          onUpdate={handleSiblingUpdate}
          onDelete={handleSiblingDelete}
          onBack={() => goTo(currentChild ? 'info' : 'profileLogin')}
          onUpgrade={() => handleUpgrade('sibling_limit')}
          onFreeSignup={handleFreeSignup}
        />
      )}

      {/* プロフィール切り替えオーバーレイ */}
      {showProfileSelect && (
        <ProfileSelect
          children={children}
          currentId={currentChild?.id}
          onSelect={handleSelectChild}
          onManage={() => { setShowProfileSelect(false); goTo('siblingManage'); }}
          onClose={() => setShowProfileSelect(false)}
        />
      )}

      {/* お知らせポップアップ */}
      {showNotice && (
        <NoticeBoard
          onClose={() => setShowNotice(false)}
          onShowAll={() => { setShowNotice(false); setShowNoticeAll(true); }}
        />
      )}
      {showNoticeAll && (
        <NoticeBoard
          showAll={true}
          onClose={() => setShowNoticeAll(false)}
        />
      )}

      {/* 前月サマリーポップアップ */}
      {monthlyRecap && (
        <MonthlyRecap
          year={monthlyRecap.year}
          month={monthlyRecap.month}
          totalDays={monthlyRecap.totalDays}
          maxStreak={monthlyRecap.maxStreak}
          onClose={() => setMonthlyRecap(null)}
        />
      )}

      {/* 連続バッジお祝いポップアップ */}
      {streakCelebration && (() => {
        const { childName, avatar, streak } = streakCelebration;
        const isZero = streak === 0;
        return (
          <div className="streak-celebration-overlay" onClick={() => setStreakCelebration(null)}>
            <div className={`streak-celebration-card${isZero ? ' streak-zero' : ''}`}>
              <div className="streak-celebration-fires">{isZero ? '😴😴😴' : '🔥🔥🔥'}</div>
              <div className="streak-celebration-avatar">{avatar}</div>
              <div className="streak-celebration-name">{childName}</div>
              {isZero ? (
                <>
                  <div className="streak-celebration-zero-msg">きょうはまだよんでないよ！</div>
                  <div className="streak-celebration-msg">いっしょにがんばろう📖</div>
                </>
              ) : (
                <>
                  <div className="streak-celebration-count">
                    <span className="streak-celebration-num">{streak}</span>
                    <span className="streak-celebration-label">にち れんぞく！</span>
                  </div>
                  <div className="streak-celebration-msg">
                    {streak >= 7 ? 'すごすぎる！かいきんきろく🏆' :
                     streak >= 3 ? 'まいにちがんばってるね✨' :
                     'いいちょうし！つづけよう⭐'}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* クレジット購入ログイン誘導ポップアップ */}
      {creditLoginPrompt && (
        <div className="credit-login-overlay" onClick={() => setCreditLoginPrompt(null)}>
          <div className="credit-login-modal" onClick={e => e.stopPropagation()}>
            <div className="credit-login-icon">🔐</div>
            <div className="credit-login-title">購入にはアカウントが必要です</div>
            <div className="credit-login-body">
              AI採点クレジット（+{creditLoginPrompt === '5' ? '5回 ¥120' : '10回 ¥200'}）を購入するには、
              無料アカウントへの登録またはログインが必要です。
              <br /><br />
              アカウントを作ると、きろくのクラウド保存や複数端末での利用も無料で使えます。
            </div>
            <div className="credit-login-btns">
              <button
                className="credit-login-btn-primary"
                onClick={() => {
                  setCreditLoginPrompt(null);
                  setAuthInitialMode('free-register');
                  goTo('auth');
                }}
              >
                無料で登録する
              </button>
              <button
                className="credit-login-btn-secondary"
                onClick={() => {
                  setCreditLoginPrompt(null);
                  setAuthInitialMode('login');
                  goTo('auth');
                }}
              >
                ログイン
              </button>
              <button
                className="credit-login-btn-cancel"
                onClick={() => setCreditLoginPrompt(null)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 決済完了メッセージ */}
      {paymentMessage === 'success' && (
        <div className="payment-toast payment-toast-success" onClick={() => setPaymentMessage(null)}>
          🎉 プレミアムへのアップグレードが完了しました！
        </div>
      )}
      {paymentMessage === 'credits_success' && (
        <div className="payment-toast payment-toast-success" onClick={() => setPaymentMessage(null)}>
          🎉 AI採点クレジットが追加されました！
        </div>
      )}
      {paymentMessage === 'cancel' && (
        <div className="payment-toast payment-toast-cancel" onClick={() => setPaymentMessage(null)}>
          決済がキャンセルされました
        </div>
      )}

    </div>
  );
}
